import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { ArrowLeft, Calendar, Info, ChevronDown, ChevronUp } from 'lucide-react';

const ConstructionCostCalculator = () => {
  const navigate = useNavigate();
  const [showTimeframe, setShowTimeframe] = useState(false);
  
  // Land loan details state
  const [landLoan, setLandLoan] = useState(200000);
  const [settlementDate, setSettlementDate] = useState('2024-07-01');
  const [term, setTerm] = useState(30);
  const [rate, setRate] = useState(6.60);
  const [repaymentType, setRepaymentType] = useState('Principal & Interest');

  // Construction details state
  const [buildPrice, setBuildPrice] = useState(600000);
  const [customerDeposit, setCustomerDeposit] = useState(50000);

  // Construction stages data
  const [stages, setStages] = useState([
    { name: 'Land loan', buildPercent: 0, days: 0, note: 'Days until construction begins' },
    { name: 'Deposit', buildPercent: 5, days: 9 },
    { name: 'Base', buildPercent: 10, days: 18 },
    { name: 'Frame', buildPercent: 15, days: 27 },
    { name: 'Lock up', buildPercent: 35, days: 63 },
    { name: 'Fixing', buildPercent: 25, days: 45 },
    { name: 'Completion', buildPercent: 10, days: 18 },
  ]);

  const [moveInDays, setMoveInDays] = useState(0);

  // Calculations
  const calculateStageAmount = (buildPercent) => {
    if (buildPercent === 0) return landLoan; // Land loan
    return (buildPercent / 100) * buildPrice;
  };

  const calculateCumulativeDrawdown = (stageIndex) => {
    let cumulative = landLoan; // Start with land loan
    for (let i = 1; i <= stageIndex; i++) { // Skip land loan (index 0)
      if (i < stages.length) {
        cumulative += calculateStageAmount(stages[i].buildPercent);
      }
    }
    return cumulative;
  };

  const calculateMonthlyLandPayment = () => {
    const monthlyRate = rate / 100 / 12;
    const numberOfPayments = term * 12;
    const payment = landLoan * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / 
                   (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
    return payment;
  };

  const calculateConstructionInterest = (month, drawdownAmount) => {
    const monthlyRate = rate / 100 / 12;
    return drawdownAmount * monthlyRate;
  };

  const generateResults = () => {
    const results = [];
    const startDate = new Date(settlementDate);
    const monthlyLandPayment = calculateMonthlyLandPayment();
    
    let cumulativeDays = 0;
    let currentDrawdown = landLoan;

    for (let month = 1; month <= 6; month++) {
      const currentDate = new Date(startDate);
      currentDate.setMonth(currentDate.getMonth() + month - 1);

      // Determine which stage we're in based on cumulative days
      let stageDrawdown = landLoan;
      for (let i = 1; i < stages.length; i++) {
        cumulativeDays += stages[i].days;
        if (cumulativeDays >= (month - 1) * 30) { // Approximate month = 30 days
          stageDrawdown = calculateCumulativeDrawdown(i);
          break;
        }
      }

      const constructionInterest = calculateConstructionInterest(month, stageDrawdown - landLoan);
      const total = monthlyLandPayment + constructionInterest;

      results.push({
        month,
        date: currentDate.toLocaleDateString('en-AU'),
        land: monthlyLandPayment,
        construction: constructionInterest,
        total: total
      });
    }
    
    return results;
  };

  const results = generateResults();
  
  const totalRepayments = results.reduce((sum, result) => sum + result.land, 0);
  const totalHoldingCosts = results.reduce((sum, result) => sum + result.construction, 0);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD',
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const formatNumber = (number) => {
    return new Intl.NumberFormat('en-AU').format(number);
  };

  return (
    <div className="min-h-screen flex flex-col bg-page">
      <Header />
      
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center gap-2"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
              <div className="flex items-center gap-2">
                <span className="text-lg font-medium text-gray-700">Quick Calcs /</span>
                <h1 className="text-lg font-semibold text-gray-900">Construction Cost Calculator</h1>
                <span className="bg-blue-100 text-blue-700 text-xs px-2 py-1 rounded-full font-medium">
                  Beta
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm">
                Create new scenario
              </Button>
              <Button variant="outline" size="sm">
                Link to scenario
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 max-w-7xl mx-auto w-full px-4 py-6">
        <div className="grid lg:grid-cols-12 gap-6 h-full">
          
          {/* Left Sidebar - Input Panel */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* Land Loan Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Land loan details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Land loan
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">$</span>
                    <Input
                      type="number"
                      value={landLoan}
                      onChange={(e) => setLandLoan(Number(e.target.value))}
                      className="pl-8"
                      placeholder="200,000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Land settlement date
                  </label>
                  <div className="relative">
                    <Input
                      type="date"
                      value={settlementDate}
                      onChange={(e) => setSettlementDate(e.target.value)}
                      className="pr-10"
                    />
                    <Calendar className="absolute right-3 top-3 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Term
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      value={term}
                      onChange={(e) => setTerm(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-600">years</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Rate
                  </label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.01"
                      value={rate}
                      onChange={(e) => setRate(Number(e.target.value))}
                      className="flex-1"
                    />
                    <span className="text-sm text-gray-600">%</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Repayment type
                  </label>
                  <div className="relative">
                    <select
                      value={repaymentType}
                      onChange={(e) => setRepaymentType(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md appearance-none bg-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Principal & Interest">Principal & Interest</option>
                      <option value="Interest Only">Interest Only</option>
                    </select>
                    <ChevronDown className="absolute right-3 top-3 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Construction Details */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg font-semibold">Construction details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Build price
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">$</span>
                    <Input
                      type="number"
                      value={buildPrice}
                      onChange={(e) => setBuildPrice(Number(e.target.value))}
                      className="pl-8"
                      placeholder="600,000"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Customer deposit
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">$</span>
                    <Input
                      type="number"
                      value={customerDeposit}
                      onChange={(e) => setCustomerDeposit(Number(e.target.value))}
                      className="pl-8"
                      placeholder="50,000"
                    />
                  </div>
                </div>

                <div>
                  <button
                    onClick={() => setShowTimeframe(!showTimeframe)}
                    className="flex items-center gap-2 text-sm font-medium text-gray-700 hover:text-gray-900"
                  >
                    Construction timeframe
                    {showTimeframe ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </button>
                  
                  {showTimeframe && (
                    <div className="mt-2 p-3 border border-gray-200 rounded-md bg-gray-50">
                      <p className="text-xs text-gray-600">
                        Construction timeline is based on the stage durations in the main calculation area.
                        Modify the days for each stage to adjust the timeframe.
                      </p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Main Panel - Calculation & Results */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Stages Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Stages</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Build (100%)</th>
                        <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">Days</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Amount</th>
                        <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Cumulative drawdown</th>
                        <th className="w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {stages.map((stage, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3">
                            <div>
                              <div className="font-medium text-gray-900">{stage.name}</div>
                              {stage.note && (
                                <div className="text-xs text-gray-500">{stage.note}</div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {stage.buildPercent > 0 && (
                              <span className="inline-flex items-center justify-center w-8 h-8 bg-gray-100 rounded-full text-sm font-medium">
                                {stage.buildPercent}
                              </span>
                            )}
                            {stage.buildPercent > 0 && (
                              <span className="ml-1 text-sm text-gray-600">%</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-center text-sm text-gray-900">
                            {stage.days}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                            {formatCurrency(calculateStageAmount(stage.buildPercent))}
                          </td>
                          <td className="px-4 py-3 text-right text-sm font-medium text-gray-900">
                            {formatCurrency(calculateCumulativeDrawdown(index))}
                          </td>
                          <td className="px-4 py-3">
                            <Info className="h-4 w-4 text-gray-400" />
                          </td>
                        </tr>
                      ))}
                      
                      {/* Move in time row */}
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-3">
                          <div>
                            <div className="font-medium text-gray-900">Move in time</div>
                            <div className="text-xs text-gray-500">Days between completion and move in</div>
                          </div>
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3 text-center">
                          <Input
                            type="number"
                            value={moveInDays}
                            onChange={(e) => setMoveInDays(Number(e.target.value))}
                            className="w-16 text-center text-sm"
                            min="0"
                          />
                        </td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3"></td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Summary Metrics */}
            <div className="grid md:grid-cols-2 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Total repayments</span>
                      <Info className="h-4 w-4 text-gray-400" />
                    </div>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(totalRepayments)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-700">Total holding costs</span>
                      <Info className="h-4 w-4 text-gray-400" />
                    </div>
                    <span className="text-lg font-semibold text-gray-900">
                      {formatCurrency(totalHoldingCosts)}
                    </span>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Results Table */}
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50">
                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Results</th>
                        {results.map((result, index) => (
                          <th key={index} className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                            Month {result.month}
                            <div className="flex items-center gap-1 justify-center">
                              <Info className="h-3 w-3 text-gray-400" />
                            </div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-3 font-medium text-gray-700">Date</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-3 text-center text-sm text-gray-900">
                            {result.date}
                          </td>
                        ))}
                      </tr>
                      
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-3 font-medium text-gray-700">Land</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                            {formatCurrency(result.land)}
                          </td>
                        ))}
                      </tr>
                      
                      <tr className="border-b border-gray-100">
                        <td className="px-4 py-3 font-medium text-gray-700">Construction</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-3 text-center text-sm font-medium text-gray-900">
                            {formatCurrency(result.construction)}
                          </td>
                        ))}
                      </tr>
                      
                      <tr className="bg-gray-50">
                        <td className="px-4 py-3 font-semibold text-gray-900">Total</td>
                        {results.map((result, index) => (
                          <td key={index} className="px-4 py-3 text-center text-sm font-semibold text-gray-900">
                            {formatCurrency(result.total)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ConstructionCostCalculator;